// Snapshots Page for Iron Forums

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { snapshotsAPI, usersAPI, monthlyGroupsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';
import {
  canHostSnapshotGroup,
  canJoinSnapshotGroup,
  hasGuestPrivileges,
  isLeadershipRole,
} from '../utils/roles';
import {
  FileText,
  Plus,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  Heart,
  Star,
  Users,
  Crown,
  KeyRound,
  LogOut,
  UserMinus,
  Clock3,
  Archive,
  Copy,
  ShieldCheck,
  Trash2,
  Printer,
  Compass,
  HelpCircle,
} from 'lucide-react';
import SnapshotHealthModal from '../components/SnapshotHealthModal';
import MySnapshotHealth from '../components/MySnapshotHealth';
import ConfirmModal from '../components/ConfirmModal';
import toast from 'react-hot-toast';
import type {
  Snapshot, SnapshotHistoryEntry, SnapshotGroup,
  MonthlySnapshotGroup, MonthlySnapshotGroupDetail,
  CurrentPeriodResponse,
} from '../types';

function apiErrorMessage(error: unknown, fallback: string): string {
  const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}
import {
  currentSnapshotMonth,
  isAllowedSnapshotMonth,
  nextSnapshotMonth,
  snapshotMonthLabel,
} from '../lib/snapshotMonths';
import { benchmarksIncomplete, canStartSnapshotCheckIn, definitionProgress } from '../lib/snapshotHealth';
import {
  SCHEMA_STATE_DIRECTION,
  STATE_THRIVING,
  isAttention,
  isCelebrate,
} from '../lib/snapshotFlags';

type TabType = 'my' | 'group';

function startDraftConflict(error: unknown): { code?: string; snapshotId?: number } {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data) return {};
  return {
    code: typeof data.code === 'string' ? data.code : undefined,
    snapshotId: typeof data.snapshot_id === 'number' ? data.snapshot_id : undefined,
  };
}

export default function Snapshots() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshotNotice] = useState<'draft' | 'submitted' | null>(() => {
    const value = (location.state as { snapshotNotice?: unknown } | null)?.snapshotNotice;
    return value === 'draft' || value === 'submitted' ? value : null;
  });
  const { user } = useAuth();
  const canJoinGroup = canJoinSnapshotGroup(user);
  const canHostGroup = canHostSnapshotGroup(user);
  
  // Tab follows the URL so Sharpen → Snapshot Group always lands on Group,
  // including when this page is already mounted on My Snapshots.
  // Invited guests can open Group; paused/limited members cannot.
  const activeTab: TabType = canJoinGroup && searchParams.get('tab') === 'group' ? 'group' : 'my';
  
  // My Snapshots state (existing)
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [history, setHistory] = useState<SnapshotHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSummaries, setExpandedSummaries] = useState<Set<number>>(new Set());
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [replaceDraft, setReplaceDraft] = useState<Snapshot | null>(null);
  const [deleteDraft, setDeleteDraft] = useState<Snapshot | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const [benchmarkPrompt, setBenchmarkPrompt] = useState<'picker' | string | null>(null);
  const benchmarksAcknowledged = useRef(false);
  const [healthPeriod, setHealthPeriod] = useState<CurrentPeriodResponse | null>(null);
  const [revisePrompt, setRevisePrompt] = useState<{ month: string; snapshotId: number } | null>(null);
  
  // Legacy leader-led groups (retained as read-only history)
  const [groups, setGroups] = useState<SnapshotGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  // Monthly snapshot groups (4-digit code, open to anyone, no per-month limit)
  const [monthlyGroups, setMonthlyGroups] = useState<MonthlySnapshotGroup[]>([]);
  const [monthlyDetails, setMonthlyDetails] = useState<MonthlySnapshotGroupDetail[]>([]);
  // A group opened from the facilitator-oversight list (not one of my own).
  const [oversightDetail, setOversightDetail] = useState<MonthlySnapshotGroupDetail | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [groupBusy, setGroupBusy] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState<number | null>(null);
  const [removeMember, setRemoveMember] = useState<{
    groupId: number;
    userId: number;
    name: string;
    isHost: boolean;
    othersRemain: boolean;
  } | null>(null);
  const [facilitatorGroups, setFacilitatorGroups] = useState<MonthlySnapshotGroup[]>([]);
  const isFacilitator = isLeadershipRole(user?.role);
  const activeDrafts = snapshots.filter(
    (snapshot) => !snapshot.is_finalized && !snapshot.superseded_at,
  );
  const finalizedSnapshots = snapshots.filter((snapshot) => snapshot.is_finalized);
  const thisMonth = currentSnapshotMonth();
  const nextMonth = nextSnapshotMonth(thisMonth);
  const healthProgress = definitionProgress(healthPeriod);
  const definitionsReady = canStartSnapshotCheckIn(healthPeriod);

  const refreshHealthPeriod = useCallback(async () => {
    try {
      setHealthPeriod(await snapshotsAPI.getCurrentPeriod());
    } catch {
      setHealthPeriod(null);
    }
  }, []);

  useEffect(() => {
    if (snapshotNotice) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, navigate, snapshotNotice]);
  
  const toggleSummary = (snapshotId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigating to detail page
    setExpandedSummaries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(snapshotId)) {
        newSet.delete(snapshotId);
      } else {
        newSet.add(snapshotId);
      }
      return newSet;
    });
  };

  const handleTabChange = (tab: TabType) => {
    if (tab === 'group') {
      setSearchParams({ tab: 'group' });
    } else {
      setSearchParams({});
    }
  };

  const loadMonthlyGroups = useCallback(async () => {
    const mine = await monthlyGroupsAPI.list();
    const active = mine.filter((g) => !g.archived);
    const details = await Promise.all(
      active.map(async (g) => {
        try {
          return await monthlyGroupsAPI.getDetail(g.id);
        } catch {
          return null;
        }
      })
    );
    let facilitator: MonthlySnapshotGroup[] = [];
    if (isFacilitator) {
      try {
        facilitator = await monthlyGroupsAPI.facilitator();
      } catch {
        facilitator = [];
      }
    }
    return {
      mine,
      details: details.filter((d): d is MonthlySnapshotGroupDetail => d !== null),
      facilitator,
    };
  }, [isFacilitator]);

  const { refresh: refreshMonthlyGroups } = useVisibilityPolling({
    fetcher: loadMonthlyGroups,
    onSuccess: ({ mine, details, facilitator }) => {
      setMonthlyGroups(mine);
      setMonthlyDetails(details);
      if (isFacilitator) setFacilitatorGroups(facilitator);
    },
    intervalMs: 8000,
    enabled: canJoinGroup && activeTab === 'group',
    refreshKey: activeTab,
  });

  // Initial data fetch - my snapshots + legacy groups.
  // Monthly snapshot groups refresh while the Group tab is open (see above).
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [snapshotsData, historyData, period] = await Promise.all([
          snapshotsAPI.getMySnapshots(),
          snapshotsAPI.getHistory(),
          snapshotsAPI.getCurrentPeriod().catch(() => null),
        ]);
        setSnapshots(snapshotsData);
        setHistory(historyData);
        setHealthPeriod(period);
        if (!hasGuestPrivileges(user)) {
          const groupsData = await usersAPI.getGroups();
          setGroups(groupsData);
          if (groupsData.length > 0 && !selectedGroupId) {
            setSelectedGroupId(groupsData[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Calculate average score from legacy ratings (v1 only)
  const getAverageScore = (ratings: Snapshot['ratings']) => {
    const scored = (ratings || []).filter((r) => r.score != null) as { score: number }[];
    if (scored.length === 0) return null;
    const sum = scored.reduce((acc, r) => acc + r.score, 0);
    return (sum / scored.length).toFixed(1);
  };

  // State distribution summary for a v2 snapshot's ratings.
  const getStateCounts = (ratings: Snapshot['ratings']) => {
    let attention = 0;
    let celebrate = 0;
    const thriving: string[] = [];
    const declining: string[] = [];
    (ratings || []).forEach((r) => {
      if (r.skipped) return;
      if (r.state === STATE_THRIVING && r.category_name) thriving.push(r.category_name);
      if (isAttention(r.state, r.direction)) {
        attention += 1;
        if (r.category_name) declining.push(r.category_name);
      } else if (isCelebrate(r.state, r.direction)) {
        celebrate += 1;
      }
    });
    return { attention, celebrate, thriving, declining };
  };

  const previousFinalized = (current: Snapshot): Snapshot | null => {
    const older = finalizedSnapshots.filter((row) => (
      row.id !== current.id
      && (
        row.month < current.month
        || (row.month === current.month && row.version < current.version)
      )
    ));
    older.sort((a, b) => b.month.localeCompare(a.month) || b.version - a.version);
    return older[0] || null;
  };

  const averageState = (snapshot: Snapshot): number | null => {
    const rated = (snapshot.ratings || []).filter((row) => !row.skipped && row.state != null);
    if (rated.length === 0) return null;
    return rated.reduce((sum, row) => sum + (row.state || 0), 0) / rated.length;
  };

  // Get trend icon based on delta
  const getTrendIcon = (delta: number | null, size: number = 20) => {
    if (delta === null || delta === 0) {
      return <Minus size={size} className="text-gray-400" />;
    }
    if (delta > 0) {
      return <TrendingUp size={size} className="text-green-500" />;
    }
    return <TrendingDown size={size} className="text-red-500" />;
  };

  // Get score color based on value
  const getScoreColor = (score: number) => {
    if (score >= 7) return 'text-green-600';
    if (score >= 4) return 'text-gold';
    return 'text-red-500';
  };

  // Get selected group object
  const selectedGroup = groups.find(g => g.id === selectedGroupId) || null;

  // Monthly snapshot group handlers
  const handleStartGroup = async () => {
    setGroupBusy(true);
    try {
      const detail = await monthlyGroupsAPI.start();
      await refreshMonthlyGroups(true);
      toast.success(`Group started — share code ${detail.code}`);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not start a group.'));
    } finally {
      setGroupBusy(false);
    }
  };

  const handleJoinGroup = async () => {
    const code = joinCode.trim();
    if (!code) return;
    setGroupBusy(true);
    try {
      await monthlyGroupsAPI.join(code);
      setJoinCode('');
      await refreshMonthlyGroups(true);
      toast.success('Joined the group.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not join with that code.'));
    } finally {
      setGroupBusy(false);
    }
  };

  const handleLeaveGroup = async (id: number) => {
    setGroupBusy(true);
    try {
      await monthlyGroupsAPI.leave(id);
      await refreshMonthlyGroups(true);
      toast.success('You left the group.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not leave the group.'));
    } finally {
      setGroupBusy(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removeMember || groupBusy) return;
    const { groupId, userId } = removeMember;
    setGroupBusy(true);
    try {
      const detail = await monthlyGroupsAPI.removeMember(groupId, userId);
      setRemoveMember(null);
      if (oversightDetail?.id === groupId) setOversightDetail(detail);
      await refreshMonthlyGroups(true);
      toast.success('Removed from the group.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not remove that member.'));
    } finally {
      setGroupBusy(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (deleteGroupId == null || groupBusy) return;
    const id = deleteGroupId;
    setGroupBusy(true);
    try {
      await monthlyGroupsAPI.remove(id);
      setDeleteGroupId(null);
      if (oversightDetail?.id === id) setOversightDetail(null);
      await refreshMonthlyGroups(true);
      toast.success('Snapshot group deleted.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not delete the group.'));
    } finally {
      setGroupBusy(false);
    }
  };

  const continueDraft = (draft: Snapshot) => {
    navigate(`/snapshots/new?draft=${draft.id}&month=${draft.month}`);
  };

  const handleNewSnapshot = () => {
    if (
      activeDrafts.length === 0
      && benchmarksIncomplete(healthPeriod)
      && !benchmarksAcknowledged.current
    ) {
      setBenchmarkPrompt('picker');
      return;
    }
    setMonthPickerOpen(true);
  };

  const confirmWithoutBenchmarks = () => {
    const prompt = benchmarkPrompt;
    benchmarksAcknowledged.current = true;
    setBenchmarkPrompt(null);
    if (prompt === 'picker') {
      setMonthPickerOpen(true);
      return;
    }
    if (prompt) void startForMonth(prompt);
  };

  const startForMonth = async (month: string) => {
    const existing = activeDrafts.find((draft) => draft.month === month);
    setMonthPickerOpen(false);
    if (existing) {
      continueDraft(existing);
      return;
    }
    if (benchmarksIncomplete(healthPeriod) && !benchmarksAcknowledged.current) {
      setBenchmarkPrompt(month);
      return;
    }
    setDraftBusy(true);
    try {
      const draft = await snapshotsAPI.startDraft(false, month);
      const benchmarks = benchmarksAcknowledged.current ? '&benchmarks=continue' : '';
      navigate(`/snapshots/new?draft=${draft.id}&month=${draft.month}${benchmarks}`);
    } catch (error) {
      const conflict = startDraftConflict(error);
      if (conflict.code === 'month_already_finalized' && conflict.snapshotId) {
        setRevisePrompt({ month, snapshotId: conflict.snapshotId });
        return;
      }
      toast.error(apiErrorMessage(error, 'Could not start a Snapshot.'));
    } finally {
      setDraftBusy(false);
    }
  };

  const startRevisedVersion = async () => {
    if (!revisePrompt) return;
    setDraftBusy(true);
    try {
      const draft = await snapshotsAPI.reviseSnapshot(revisePrompt.snapshotId);
      setRevisePrompt(null);
      navigate(`/snapshots/new?draft=${draft.id}&month=${draft.month}`);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not start a new version.'));
    } finally {
      setDraftBusy(false);
    }
  };

  const startNewDraft = async () => {
    if (!replaceDraft) return;
    setDraftBusy(true);
    try {
      const draft = await snapshotsAPI.startDraft(true, replaceDraft.month);
      setReplaceDraft(null);
      toast.success('Previous draft deleted. Starting a new Snapshot.');
      navigate(`/snapshots/new?draft=${draft.id}&month=${draft.month}`);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not start a new Snapshot.'));
    } finally {
      setDraftBusy(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!deleteDraft || draftBusy) return;
    const id = deleteDraft.id;
    setDraftBusy(true);
    try {
      await snapshotsAPI.deleteSnapshot(id);
      setDeleteDraft(null);
      const [snapshotsData, historyData] = await Promise.all([
        snapshotsAPI.getMySnapshots(),
        snapshotsAPI.getHistory(),
      ]);
      setSnapshots(snapshotsData);
      setHistory(historyData);
      toast.success('Draft deleted.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not delete that draft.'));
    } finally {
      setDraftBusy(false);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success('Code copied'),
      () => toast.error('Could not copy'),
    );
  };

  // Detail card for one monthly snapshot group (mine or oversight-opened).
  const renderGroupDetail = (detail: MonthlySnapshotGroupDetail) => (
    <div key={detail.id} className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-navy">{detail.title}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {[detail.forum_name, detail.month_display].filter(Boolean).join(' · ')} ·{' '}
            {detail.member_count}/{detail.max_members} members
          </p>
          {detail.closes_label && (
            <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5">
              <Clock3 size={14} />
              {detail.closes_label}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(detail.is_host || (detail.can_remove_members && detail.is_open && !detail.archived)) && (
            <button
              type="button"
              onClick={() => setDeleteGroupId(detail.id)}
              disabled={groupBusy}
              className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-700 rounded-lg hover:bg-red-50 transition disabled:opacity-50"
            >
              <Trash2 size={16} />
              Delete group
            </button>
          )}
          {!detail.is_host && detail.members.some((m) => m.is_self) && detail.is_open && !detail.archived && (
            <button
              onClick={() => handleLeaveGroup(detail.id)}
              disabled={groupBusy}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              <LogOut size={16} />
              Leave
            </button>
          )}
          {!detail.is_host && !detail.members.some((m) => m.is_self) && (
            <span className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded-lg">
              <ShieldCheck size={16} />
              Viewing as facilitator
            </span>
          )}
        </div>
      </div>

      {/* Join code — shown prominently to the host to share */}
      {detail.is_host && (
        <div className="flex items-center justify-between gap-3 bg-navy/5 rounded-xl p-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Join code</p>
            <p className="text-3xl font-bold text-navy tracking-[0.3em]">{detail.code}</p>
            <p className="text-xs text-gray-500 mt-1">
              Share with anyone you'd like to join — any Forum, any role. They open{' '}
              <span className="font-medium">Snapshots → Group</span> and enter this code under{' '}
              <span className="font-medium">“Join a monthly snapshot group.”</span>{' '}
              {detail.closes_label}. Started before 2:00 PM in your timezone, a group closes
              at 2:00 PM. Started at 2:00 PM or later, it stays open for 3 hours. After it
              closes, shared snapshots are hidden from everyone.
            </p>
          </div>
          <button
            onClick={() => copyCode(detail.code)}
            className="flex items-center gap-1.5 px-3 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition"
          >
            <Copy size={16} />
            Copy
          </button>
        </div>
      )}

      {/* Members + each member's most-recent finalized snapshot (read-only) */}
      <div>
        <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
          <Users size={18} />
          Members ({detail.members.length})
        </h3>
        {!detail.snapshots_visible && (
          <p className="text-sm text-gray-500 mb-3">Shared snapshots from this group are closed.</p>
        )}
        <div className="space-y-3">
          {detail.members.map((m) => {
            const snap = m.snapshot;
            const isV2 = snap?.schema_version === SCHEMA_STATE_DIRECTION;
            const counts = snap ? getStateCounts(snap.ratings) : { attention: 0, celebrate: 0 };
            return (
              <div
                key={m.id}
                onClick={snap ? () => navigate(`/snapshots/${snap.id}?from=group`) : undefined}
                className={`border border-gray-100 rounded-xl p-4 ${
                  snap ? 'cursor-pointer hover:border-navy/30 hover:bg-navy/[0.02] transition' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-navy">{m.name}</span>
                    {m.is_host && <Crown size={14} className="text-gold" />}
                    {m.is_self && <span className="text-xs text-gold">(You)</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {detail.can_remove_members && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemoveMember({
                            groupId: detail.id,
                            userId: m.id,
                            name: m.name,
                            isHost: m.is_host,
                            othersRemain: detail.members.length > 1,
                          });
                        }}
                        disabled={groupBusy}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-red-200 text-red-700 rounded-lg hover:bg-red-50 transition disabled:opacity-50"
                      >
                        <UserMinus size={14} />
                        Remove
                      </button>
                    )}
                    {snap && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        {snap.month_display} · finalized
                        <ChevronRight size={14} />
                      </span>
                    )}
                  </div>
                </div>
                {snap ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    {isV2 ? (
                      <>
                        {counts.celebrate > 0 && (
                          <span className="flex items-center gap-1 bg-green-50 text-green-700 px-2.5 py-1 rounded-full">
                            <Star size={14} /> {counts.celebrate} to celebrate
                          </span>
                        )}
                        {counts.attention > 0 && (
                          <span className="flex items-center gap-1 bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                            <TrendingDown size={14} /> {counts.attention} need attention
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="flex items-center gap-1 bg-navy/5 text-navy px-2.5 py-1 rounded-full">
                        Avg {getAverageScore(snap.ratings) || '-'}
                      </span>
                    )}
                    {snap.most_important_category_name && (
                      <span className="flex items-center gap-1 bg-navy/5 text-navy px-2.5 py-1 rounded-full">
                        <Star size={14} className="text-gold" />
                        {snap.most_important_category_name}
                      </span>
                    )}
                    {snap.major_issue && (
                      <span className="flex items-center gap-1 text-gray-500">
                        <Heart size={14} className="text-gold" />
                        <span className="line-clamp-1 max-w-xs">{snap.major_issue}</span>
                      </span>
                    )}
                  </div>
                ) : detail.snapshots_visible ? (
                  <p className="mt-1 text-sm text-gray-400">No finalized snapshot shared yet this month.</p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {snapshotNotice && (
        <div role="status" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-800 font-medium">
          {snapshotNotice === 'submitted'
            ? 'Snapshot submitted successfully.'
            : 'Snapshot draft saved. You can continue it anytime.'}
        </div>
      )}
      {monthPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-navy">Which month?</h2>
            <p className="text-gray-600">
              Start this month, or get next month ready before the 1st. You can keep a
              draft for each.
            </p>
            <div className="flex flex-col gap-3 pt-2">
              <button
                type="button"
                onClick={() => void startForMonth(thisMonth)}
                disabled={draftBusy}
                className="w-full px-4 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                {snapshotMonthLabel(thisMonth)}
                {activeDrafts.some((d) => d.month === thisMonth) ? ' — resume draft' : ''}
              </button>
              <button
                type="button"
                onClick={() => void startForMonth(nextMonth)}
                disabled={draftBusy}
                className="w-full px-4 py-3 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition disabled:opacity-50"
              >
                {snapshotMonthLabel(nextMonth)}
                {activeDrafts.some((d) => d.month === nextMonth) ? ' — resume draft' : ''}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMonthPickerOpen(false)}
              className="w-full text-sm text-gray-500 hover:text-navy transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {revisePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-navy">Start a new version?</h2>
            <p className="text-gray-600">
              You already submitted {snapshotMonthLabel(revisePrompt.month)}. Starting a new
              version copies your answers so you can revise — it will not wipe the submitted
              snapshot.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => setRevisePrompt(null)}
                className="flex-1 px-4 py-3 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition"
              >
                Keep submitted
              </button>
              <button
                type="button"
                onClick={() => void startRevisedVersion()}
                disabled={draftBusy}
                className="flex-1 px-4 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                Start new version
              </button>
            </div>
          </div>
        </div>
      )}
      {replaceDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-navy">Start a new draft?</h2>
            <p className="text-gray-600">
              This permanently deletes your {snapshotMonthLabel(replaceDraft.month)} draft.
              Finalized Snapshots are not affected.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => continueDraft(replaceDraft)}
                className="flex-1 px-4 py-3 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition"
              >
                Resume instead
              </button>
              <button
                type="button"
                onClick={() => void startNewDraft()}
                disabled={draftBusy}
                className="flex-1 px-4 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                Start New
              </button>
            </div>
            <button
              type="button"
              onClick={() => setReplaceDraft(null)}
              className="w-full text-sm text-gray-500 hover:text-navy transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            Snapshots
          </h1>
          <p className="text-gray-600">
            {activeTab === 'my' && 'Your monthly growth reflections'}
            {activeTab === 'group' && 'Your group, members, and team snapshots'}
          </p>
        </div>
        {activeTab === 'my' && (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/snapshots/blank"
              className="inline-flex items-center gap-2 px-4 py-3 text-navy border border-navy rounded-xl hover:bg-navy hover:text-white transition font-medium"
            >
              <Printer size={20} />
              Print blank Snapshot
            </Link>
            <button
              onClick={handleNewSnapshot}
              className="inline-flex items-center gap-2 px-5 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-semibold text-lg"
            >
              <Plus size={24} />
              {activeDrafts.length > 0 ? 'Continue Snapshot' : 'New Snapshot'}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-px overflow-x-auto">
        <button
          onClick={() => handleTabChange('my')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition whitespace-nowrap ${
            activeTab === 'my'
              ? 'text-navy border-b-2 border-navy -mb-px'
              : 'text-gray-500 hover:text-navy'
          }`}
        >
          <FileText size={18} />
          <span>My Snapshots</span>
        </button>
        {canJoinGroup && (
        <button
          onClick={() => handleTabChange('group')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition whitespace-nowrap ${
            activeTab === 'group'
              ? 'text-navy border-b-2 border-navy -mb-px'
              : 'text-gray-500 hover:text-navy'
          }`}
        >
          <Users size={18} />
          <span>Group</span>
        </button>
        )}
      </div>

      {/* Group Dropdown (for group tab when user has multiple groups) */}
      {activeTab === 'group' && groups.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Viewing:</span>
          <select
            value={selectedGroupId || ''}
            onChange={(e) => setSelectedGroupId(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-navy font-medium"
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* MY SNAPSHOTS TAB */}
      {activeTab === 'my' && (
        <>
          {activeDrafts.map((draft) => (
            <div key={draft.id} className="rounded-2xl bg-navy text-white p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm text-white/70">In progress</p>
                  <h2 className="text-xl font-bold mt-1">
                    {snapshotMonthLabel(draft.month)} Snapshot
                  </h2>
                  <p className="text-sm text-white/80 mt-1">
                    {draft.wizard_progress}% complete · last saved{' '}
                    {new Date(draft.updated_at).toLocaleString()}
                  </p>
                  <div className="w-full max-w-md bg-white/20 rounded-full h-2 mt-3">
                    <div
                      className="bg-gold h-2 rounded-full"
                      style={{ width: `${draft.wizard_progress}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => continueDraft(draft)}
                    className="px-5 py-3 bg-white text-navy rounded-xl font-semibold hover:bg-off-white transition"
                  >
                    Resume
                  </button>
                  {isAllowedSnapshotMonth(draft.month) && (
                    <button
                      type="button"
                      onClick={() => setReplaceDraft(draft)}
                      disabled={draftBusy}
                      className="px-5 py-3 border border-white/50 text-white rounded-xl font-semibold hover:bg-white/10 transition disabled:opacity-50"
                    >
                      Start New
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setDeleteDraft(draft)}
                    disabled={draftBusy}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-white/50 text-white rounded-xl font-semibold hover:bg-white/10 transition disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-white rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-navy flex items-center gap-2">
                <Compass size={18} />
                Snapshot Health Definitions
                {healthProgress && !healthProgress.complete && (
                  <span className="text-xs font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                    Incomplete
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {healthProgress && !healthProgress.complete
                  ? `${healthProgress.filled} of ${healthProgress.total} areas defined. You can keep going, or fill these in first.`
                  : 'What healthy looks like in each area — these anchor your monthly Snapshot.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setHealthModalOpen(true)}
              className={
                definitionsReady
                  ? 'shrink-0 px-4 py-2 border border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition'
                  : 'shrink-0 px-5 py-3 bg-navy text-white rounded-xl font-semibold hover:bg-navy-dark transition'
              }
            >
              {definitionsReady ? 'Edit Definitions' : 'Set definitions'}
            </button>
          </div>

          <SnapshotHealthModal
            isOpen={healthModalOpen}
            onClose={() => setHealthModalOpen(false)}
            onSaved={refreshHealthPeriod}
          />

          {/* Personal State/Direction radar + trend (Snapshot 2.0 history) —
              the member-scoped twin of the leadership dashboard's Forum
              Health card. Renders nothing until a v2 snapshot exists. */}
          <MySnapshotHealth history={history} />

          {/* Snapshot List - Insightful Cards */}
      {finalizedSnapshots.length > 0 ? (
        <div className="space-y-4">
          {finalizedSnapshots.map((snapshot) => {
            const isV2 = snapshot.schema_version === SCHEMA_STATE_DIRECTION;
            const avgScore = getAverageScore(snapshot.ratings);
            const avgScoreNum = avgScore ? parseFloat(avgScore) : 5;
            const stateCounts = getStateCounts(snapshot.ratings);
            const previous = isV2 ? previousFinalized(snapshot) : null;
            const currentAvg = isV2 ? averageState(snapshot) : null;
            const previousAvg = previous ? averageState(previous) : null;
            const stateDelta = (
              currentAvg != null && previousAvg != null
                ? currentAvg - previousAvg
                : null
            );

            return (
              <div
                key={snapshot.id}
                onClick={() => navigate(`/snapshots/${snapshot.id}`)}
                className="bg-white rounded-xl shadow-sm hover:shadow-md hover:border-navy border border-transparent transition cursor-pointer group overflow-hidden"
              >
                {/* Header Row */}
                <div className="flex items-center justify-between p-5 pb-3">
                  <h3 className="font-bold text-navy text-xl">
                    {snapshot.month_display}
                  </h3>
                  
                  <div className="flex items-center gap-4">
                    {!isV2 && avgScore && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm">Avg</span>
                        <span className={`text-3xl font-bold ${getScoreColor(avgScoreNum)}`}>
                          {avgScore}
                        </span>
                        {getTrendIcon(snapshot.overall_delta)}
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/snapshots/${snapshot.id}?print=1`);
                      }}
                      className="p-2 text-gray-400 hover:text-navy hover:bg-navy/5 rounded-lg transition"
                      title="Print / Save PDF"
                      aria-label="Print snapshot"
                    >
                      <Printer size={20} />
                    </button>
                    <ChevronRight className="text-gray-300 group-hover:text-navy transition" size={28} />
                  </div>
                </div>

                {/* Insights: v2 State distribution + most important */}
                {isV2 && (
                  <div className="px-5 pb-3 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {stateDelta != null && (
                        <div className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-full ${
                          stateDelta > 0.15
                            ? 'bg-green-50 text-green-700'
                            : stateDelta < -0.15
                              ? 'bg-red-50 text-red-700'
                              : 'bg-gray-50 text-gray-600'
                        }`}>
                          {stateDelta > 0.15 ? (
                            <TrendingUp size={16} />
                          ) : stateDelta < -0.15 ? (
                            <TrendingDown size={16} />
                          ) : (
                            <Minus size={16} />
                          )}
                          <span className="font-medium">
                            {stateDelta > 0.15
                              ? `Up ${stateDelta.toFixed(1)} vs last`
                              : stateDelta < -0.15
                                ? `Down ${Math.abs(stateDelta).toFixed(1)} vs last`
                                : 'Holding vs last'}
                          </span>
                        </div>
                      )}
                      {snapshot.most_important_category_name && (
                        <div className="flex items-center gap-2 text-sm bg-navy/5 text-navy px-3 py-1.5 rounded-full">
                          <Star size={16} className="text-gold" />
                          <span className="font-medium">{snapshot.most_important_category_name}</span>
                        </div>
                      )}
                    </div>
                    {stateCounts.thriving.length > 0 && (
                      <p className="text-sm text-green-700">
                        <span className="font-medium">Thriving:</span>{' '}
                        {stateCounts.thriving.join(', ')}
                      </p>
                    )}
                    {stateCounts.declining.length > 0 && (
                      <p className="text-sm text-red-700">
                        <span className="font-medium">Needs attention:</span>{' '}
                        {stateCounts.declining.join(', ')}
                      </p>
                    )}
                    {stateCounts.thriving.length === 0 && stateCounts.declining.length === 0 && stateCounts.celebrate > 0 && (
                      <p className="text-sm text-green-700">
                        <span className="font-medium">{stateCounts.celebrate} to celebrate</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Growth Insights (legacy delta-based) */}
                {!isV2 && (snapshot.best_growth_area || snapshot.needs_attention_area) && (
                  <div className="px-5 pb-3 flex flex-wrap gap-3">
                    {snapshot.best_growth_area && (
                      <div className="flex items-center gap-2 text-sm bg-green-50 text-green-700 px-3 py-1.5 rounded-full">
                        <TrendingUp size={16} />
                        <span className="font-medium">{snapshot.best_growth_area.name}</span>
                        <span className="text-green-600">+{snapshot.best_growth_area.delta}</span>
                      </div>
                    )}
                    {snapshot.needs_attention_area && (
                      <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 px-3 py-1.5 rounded-full">
                        <TrendingDown size={16} />
                        <span className="font-medium">{snapshot.needs_attention_area.name}</span>
                        <span className="text-red-600">{snapshot.needs_attention_area.delta}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Summary - Expandable */}
                {snapshot.summary && (
                  <div className="px-5 pb-3">
                    <p className={`text-gray-600 text-sm ${!expandedSummaries.has(snapshot.id) ? 'line-clamp-2' : ''}`}>
                      {snapshot.summary}
                    </p>
                    {snapshot.summary.length > 150 && (
                      <button
                        onClick={(e) => toggleSummary(snapshot.id, e)}
                        className="text-navy text-sm font-medium mt-1 hover:underline"
                        aria-expanded={expandedSummaries.has(snapshot.id)}
                      >
                        {expandedSummaries.has(snapshot.id) ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                )}

                {/* Prayer Request */}
                {snapshot.major_issue && (
                  <div className="px-5 pb-4 pt-2 border-t border-gray-100 mt-1">
                    <div className="flex items-start gap-2">
                      <Heart size={16} className="text-gold mt-0.5 shrink-0" />
                      <p className="text-gray-500 text-sm line-clamp-1">
                        {snapshot.major_issue}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <FileText className="mx-auto text-gray-300" size={64} />
          <h3 className="text-xl font-semibold text-navy mt-4">No Finalized Snapshots Yet</h3>
          <p className="text-gray-500 mt-2 max-w-md mx-auto">
            Take 20-30 minutes to pause, pray, and honestly reflect on where you are.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <button
              onClick={handleNewSnapshot}
              className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-semibold text-lg"
            >
              <Plus size={24} />
              {activeDrafts.length > 0 ? 'Continue a Snapshot' : 'Create Your First Snapshot'}
            </button>
            <Link
              to="/snapshots/blank"
              className="inline-flex items-center gap-2 px-5 py-3 text-navy border border-navy rounded-xl hover:bg-navy hover:text-white transition font-medium"
            >
              <Printer size={20} />
              Print blank Snapshot
            </Link>
          </div>
        </div>
      )}

        </>
      )}
      {/* GROUP TAB */}
      {activeTab === 'group' && (
        <>
          {/* ===== Monthly Snapshot Groups (mine, plus any oversight-opened) ===== */}
          {monthlyDetails.map((d) => renderGroupDetail(d))}
          {oversightDetail && !monthlyDetails.some((d) => d.id === oversightDetail.id) &&
            renderGroupDetail(oversightDetail)}

          {/* Start / Join — open to everyone, any number of groups */}
          <div className={`grid gap-4 ${canHostGroup ? 'md:grid-cols-2' : ''}`}>
            {/* Start */}
            {canHostGroup && (
            <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col">
              <h2 className="font-bold text-navy flex items-center gap-2">
                <Users size={18} />
                Start a monthly snapshot group
              </h2>
              <p className="text-sm text-gray-500 mt-1 flex-1">
                Create a group and get a 4-digit code to share with anyone — any forum (or
                none). Joining shares your finalized snapshot with the group for this month —
                nothing else.
              </p>
              <button
                onClick={handleStartGroup}
                disabled={groupBusy}
                className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-semibold disabled:opacity-50"
              >
                <Plus size={20} />
                Start group
              </button>
            </div>
            )}

            {/* Join */}
            <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col">
              <h2 className="font-bold text-navy flex items-center gap-2">
                <KeyRound size={18} />
                Join a monthly snapshot group
              </h2>
              <p className="text-sm text-gray-500 mt-1 flex-1">
                Enter a 4-digit code from any group's host. You can be in as many groups as
                you like.
              </p>
              <div className="mt-4 flex gap-2">
                <input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="0000"
                  inputMode="numeric"
                  className="w-32 px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-2xl font-bold tracking-[0.3em] text-center"
                />
                <button
                  onClick={handleJoinGroup}
                  disabled={groupBusy || joinCode.trim().length < 4}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-semibold disabled:opacity-50"
                >
                  Join
                </button>
              </div>
            </div>
          </div>

          {/* Past monthly snapshot groups (archived history) */}
          {monthlyGroups.some((g) => g.archived) && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                <Archive size={18} />
                Past snapshot groups
              </h3>
              <div className="space-y-2">
                {monthlyGroups
                  .filter((g) => g.archived)
                  .map((g) => (
                    <div key={g.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">
                        {[g.forum_name, g.month_display].filter(Boolean).join(' · ')}
                      </span>
                      <span className="text-xs text-gray-400">{g.member_count} members · closed</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Facilitator oversight — all monthly groups in forums you facilitate */}
          {isFacilitator && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-semibold text-navy mb-1 flex items-center gap-2">
                <ShieldCheck size={18} />
                Facilitator oversight
                <span
                  title="Every monthly snapshot group in forums you facilitate, even if you did not join."
                  className="text-gray-400 cursor-help"
                >
                  <HelpCircle size={14} />
                </span>
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Snapshot groups in your forums, even ones you did not join.
              </p>
              {facilitatorGroups.length === 0 ? (
                <p className="text-sm text-gray-400">No snapshot groups in your forums yet.</p>
              ) : (
              <div className="space-y-2">
                {facilitatorGroups.map((g) => (
                  <Link
                    key={g.id}
                    to="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        setOversightDetail(await monthlyGroupsAPI.getDetail(g.id));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      } catch {
                        toast.error('Could not open that group.');
                      }
                    }}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                  >
                    <span className="text-sm text-gray-700">
                      {[g.forum_name, g.month_display].filter(Boolean).join(' · ')}
                      {g.archived && <span className="ml-2 text-xs text-gray-400">(closed)</span>}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      {g.member_count} members
                      <ChevronRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>
              )}
            </div>
          )}

          {/* Legacy leader-led groups: read-only archived history one-liner */}
          {selectedGroup && (
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <p className="font-semibold text-navy flex items-center gap-2">
                <Archive size={16} className="text-gray-400" />
                {selectedGroup.name}
                <span className="text-xs font-normal text-gray-400">(archived)</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {selectedGroup.members.length} members
                {selectedGroup.leader && (
                  <> · led by {selectedGroup.leader.first_name} {selectedGroup.leader.last_name}</>
                )}
                {' '}— older leader-led groups are read-only history.
              </p>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={deleteDraft != null}
        onCancel={() => setDeleteDraft(null)}
        onConfirm={() => void handleDeleteDraft()}
        title="Delete this draft?"
        message={
          deleteDraft
            ? `This removes your ${snapshotMonthLabel(deleteDraft.month)} in-progress Snapshot. Finalized Snapshots stay. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete draft"
        variant="danger"
      />
      <ConfirmModal
        isOpen={deleteGroupId != null}
        onCancel={() => setDeleteGroupId(null)}
        onConfirm={handleDeleteGroup}
        title="Delete this snapshot group?"
        message="This removes the group and its join code. Anyone who already joined will lose access. This cannot be undone."
        confirmLabel="Delete group"
        variant="danger"
      />
      <ConfirmModal
        isOpen={removeMember != null}
        onCancel={() => setRemoveMember(null)}
        onConfirm={() => void handleRemoveMember()}
        title="Remove from this group?"
        message={
          removeMember
            ? removeMember.isHost && removeMember.othersRemain
              ? `Remove ${removeMember.name}, who started this group, for everyone? The next member will hold the join code. Their own snapshot stays. They can join again with the code while the group is still open.`
              : `Remove ${removeMember.name} from this snapshot group for everyone? Their own snapshot stays. If the group is still open, they can join again with the code.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
      />
      <ConfirmModal
        isOpen={benchmarkPrompt != null}
        onCancel={() => setBenchmarkPrompt(null)}
        onConfirm={confirmWithoutBenchmarks}
        title="Continue without benchmarks?"
        message="Some areas still don’t say what healthy looks like. You can set those benchmarks now, or continue and fill them in later. Guests can set them too."
        confirmLabel="Continue"
        variant="primary"
      />
    </div>
  );
}
